import React from 'react';
import {
  BookOpen,
  User,
  Building,
  GitBranch,
  FileText,
  Globe,
  ShoppingCart,
  ExternalLink,
  Phone,
  MessageCircle,
  AlertTriangle,
  UserCheck,
  Building2,
} from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { useAuthStore } from '../../../auth/store/useAuthStore.js';
import { isAuthorSubmission } from '../book-list/bookListUtils.js';
import { LazyBookCover } from '../../../../shared/components/LazyBookCover.js';
import { resolveCoverUrl } from '../../../../shared/utils/imageCompressor.js';
import { slugify } from '../../../../shared/utils/stringUtils.js';

interface BookDetailHeroProps {
  book: Book;
}

export const BookDetailHero: React.FC<BookDetailHeroProps> = ({ book }) => {
  const { user } = useAuthStore();
  const isFromAuthor = isAuthorSubmission(book, user);
  const resolvedCoverUrl = resolveCoverUrl(book.coverUrl);

  return (
    <div className="flex flex-col sm:flex-row gap-6 lg:gap-8 items-start">
      {/* Book Cover Image */}
      <LazyBookCover
        coverUrl={resolvedCoverUrl}
        title={book.title}
        className="w-28 sm:w-36 lg:w-44 xl:w-52 aspect-2/3 shrink-0 rounded-2xl shadow-lg border border-slate-200/80"
        targetWidth={500}
        quality={85}
      />

      {/* Book Info */}
      <div className="flex-1 min-w-0 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs sm:text-sm font-semibold">
            {book.category}
          </span>

          {/* Submission Source Badge */}
          {user?.role === 'penerbit' && (
            isFromAuthor ? (
              <span className="px-2.5 py-1 rounded-lg bg-purple-100 text-purple-900 border border-purple-200 text-xs font-bold inline-flex items-center gap-1.5 shadow-2xs">
                <UserCheck className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                <span>Diajukan oleh Penulis</span>
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 border border-slate-200 text-xs font-bold inline-flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span>Pendaftaran Mandiri Penerbit</span>
              </span>
            )
          )}

          <span className="text-xs sm:text-sm text-slate-400 font-medium">Tahun {book.publicationYear}</span>
        </div>

        <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-slate-900 leading-tight">
          {book.title}
        </h2>
        {book.subtitle && (
          <p className="text-sm sm:text-base lg:text-lg font-medium text-indigo-700 leading-snug">
            {book.subtitle}
          </p>
        )}
        {book.originalTitle && (
          <p className="text-xs sm:text-sm text-slate-500 italic">
            Judul Asli: {book.originalTitle}
          </p>
        )}

        {/* Author & Publisher Information - Full Width & Wrapping */}
        <div className="pt-2 space-y-2 text-xs sm:text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {/* Penulis */}
            <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex items-start justify-between gap-2">
              <div className="flex items-start gap-2.5 min-w-0">
                <div className="w-6 h-6 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center shrink-0 mt-0.5">
                  <User className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                    Penulis
                  </span>
                  <span className="font-semibold text-slate-900 text-xs sm:text-sm break-words leading-snug block">
                    {book.authorName}
                  </span>
                  <a
                    href={`/penulis/${slugify(book.authorName)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-indigo-600 font-semibold hover:underline inline-flex items-center gap-0.5 mt-0.5"
                  >
                    <span>Katalog Publik Penulis</span> &rarr;
                  </a>
                </div>
              </div>
            </div>

            {/* Penerbit */}
            <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex items-start justify-between gap-2">
              <div className="flex items-start gap-2.5 min-w-0">
                <div className="w-6 h-6 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center shrink-0 mt-0.5">
                  <Building className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                    Penerbit
                  </span>
                  <span className="font-semibold text-slate-900 text-xs sm:text-sm break-words leading-snug block">
                    {book.publisherName}
                  </span>
                  <a
                    href={`/penerbit/${slugify(book.publisherName)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-indigo-600 font-semibold hover:underline inline-flex items-center gap-0.5 mt-0.5"
                  >
                    <span>Katalog Publik Penerbit</span> &rarr;
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Author WhatsApp Contact Info */}
          {book.authorWhatsapp && (
            <div className="p-2.5 rounded-xl bg-emerald-50/80 border border-emerald-200/80 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-7 h-7 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-2xs">
                  <Phone className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] text-emerald-800 font-bold uppercase tracking-wider block">
                    Kontak WhatsApp Penulis
                  </span>
                  <span className="font-mono text-emerald-900 font-semibold text-xs truncate block">
                    {book.authorWhatsapp}
                  </span>
                </div>
              </div>
              <a
                href={`https://wa.me/${book.authorWhatsapp.replace(/[^0-9]/g, '').replace(/^0/, '62')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 transition shadow-2xs shrink-0 cursor-pointer"
                title="Buka percakapan WhatsApp"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>Chat WA</span>
              </a>
            </div>
          )}

          {/* Feedback / Rejection Note banner */}
          {(book.status === 'draft' || book.status === 'ditolak' || Boolean(book.editionNotes)) && (
            <div
              className={`p-3 rounded-xl border text-xs space-y-1.5 shadow-2xs ${
                book.status === 'ditolak'
                  ? 'bg-rose-50 border-rose-200 text-rose-950'
                  : 'bg-amber-50 border-amber-300 text-amber-950 ring-1 ring-amber-400/20'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div
                  className={`flex items-center gap-1.5 font-bold text-[11px] uppercase tracking-wide ${
                    book.status === 'ditolak' ? 'text-rose-800' : 'text-amber-900'
                  }`}
                >
                  <AlertTriangle
                    className={`w-4 h-4 shrink-0 ${
                      book.status === 'ditolak' ? 'text-rose-600' : 'text-amber-600'
                    }`}
                  />
                  <span>
                    {user?.role === 'penerbit'
                      ? book.status === 'ditolak'
                        ? 'Naskah Telah Ditolak Redaksi'
                        : 'Catatan Penolakan / Revisi yang Anda Berikan'
                      : book.status === 'ditolak'
                      ? 'Pengajuan UIBN Ditolak'
                      : book.editionNotes
                      ? 'Catatan Revisi / Masukan Penerbit'
                      : 'Naskah Berstatus Draf (Perlu Tindakan)'}
                  </span>
                </div>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0 ${
                    book.status === 'ditolak'
                      ? 'bg-rose-200 text-rose-800'
                      : 'bg-amber-200 text-amber-900'
                  }`}
                >
                  {book.status === 'ditolak' ? 'Ditolak' : 'Perlu Revisi'}
                </span>
              </div>

              <p className="text-slate-800 bg-white/95 p-2.5 rounded-lg border border-amber-200/80 font-mono text-xs leading-relaxed break-words">
                {book.editionNotes ||
                  (user?.role === 'penerbit'
                    ? 'Naskah ini telah ditolak oleh redaksi Anda. Menunggu Penulis melakukan perbaikan metadata dan mengajukan ulang.'
                    : book.status === 'ditolak'
                    ? 'Pengajuan naskah ini ditolak oleh verifikator. Silakan tinjau kembali metadata atau perbaiki naskah sebelum mengajukan ulang.'
                    : book.publisherId
                    ? 'Naskah berada dalam status draf untuk kurasi penerbit terdaftar. Silakan lengkapi atau revisi metadata naskah lalu klik tombol Kirim Ulang ke Penerbit.'
                    : 'Naskah tersimpan sebagai draf. Hak pengeditan metadata naskah dibuka penuh.')}
              </p>
            </div>
          )}

          {/* Additional Metadata Pills */}
          <div className="flex flex-wrap items-center gap-2 pt-1 text-slate-600">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-800 text-xs font-semibold border border-indigo-200/60">
              <GitBranch className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
              <span>{book.edition || 'Edisi 1'}</span>
              {book.editionType && (
                <span className="text-[10px] uppercase tracking-wider bg-white/80 px-1.5 py-0.2 rounded text-indigo-700">
                  {book.editionType.replace('_', ' ')}
                </span>
              )}
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100/80 text-slate-700 text-xs">
              <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span>Halaman: <strong>{book.pages ? `${book.pages} hlm` : '-'}</strong></span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100/80 text-slate-700 text-xs">
              <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span>Bahasa: <strong>{book.language || 'Indonesia'}</strong></span>
            </div>
            {book.isbnOriginal && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100/80 text-slate-700 text-xs font-mono">
                <span>ISBN Asal: {book.isbnOriginal}</span>
              </div>
            )}
            {book.purchaseUrl && (
              <a
                href={book.purchaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition shadow-xs cursor-pointer"
                title="Buka Halaman Pembelian Buku Resmi"
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                <span>Beli Buku Resmi</span>
                <ExternalLink className="w-3 h-3 opacity-80" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
